import { matchTopic, normalize, TOPIC_LIMITS, type AgentDraft } from "@nemo/agent-schema";
import { useState } from "react";
import { useI18n } from "../i18n.js";
import { isGenericTrigger } from "../readiness/checks.js";
import { PANEL } from "../readiness/text.js";
import { TOPICS_TEXT, hasPlaceholder } from "../readiness/topics-text.js";
import { Field, ListEditor, Section, TextInput, Toggle } from "./ui.js";

type Topic = NonNullable<AgentDraft["knowledge"]>["topics"][number];

/** Triggers of a later topic that an earlier one already covers: whatever contains the later phrase contains the earlier one, so the earlier topic answers first. */
export function shadowed(topics: Topic[], index: number): { trigger: string; by: string }[] {
  const out: { trigger: string; by: string }[] = [];
  const me = topics[index];
  if (!me || me.enabled === false) return out;
  for (const trigger of me.triggers) {
    const n = ` ${normalize(trigger)} `;
    if (n.trim() === "") continue;
    for (let i = 0; i < index; i++) {
      const other = topics[i] as Topic;
      if (other.enabled === false) continue;
      if (other.triggers.some((o) => normalize(o) !== "" && n.includes(` ${normalize(o)} `))) {
        out.push({ trigger, by: other.title });
        break;
      }
    }
  }
  return out;
}

/**
 * Topics: exact answers the agent sends word for word, without the AI, when a visitor's message contains one of the
 * trigger phrases. The first matching topic in the list answers. "Test" runs the very code the chat runs.
 */
export function TopicsEditor({ topics, onChange }: { topics: Topic[]; onChange: (next: Topic[]) => void }) {
  const { t, lang } = useI18n();
  const [probe, setProbe] = useState("");
  const [moved, setMoved] = useState("");
  const set = (i: number, patch: Partial<Topic>) => onChange(topics.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= topics.length) return;
    const next = [...topics];
    [next[i], next[j]] = [next[j] as Topic, next[i] as Topic];
    onChange(next);
    setMoved(t("topics.moved", { title: (topics[i] as Topic).title || String(i + 1), n: String(j + 1) }));
  };
  const hit = probe.trim() ? matchTopic(probe, topics.filter((x) => x.title.trim() && x.triggers.length && x.answer.trim())) : null;

  return (
    <Section title={t("topics.title")} help={PANEL.help.topics[lang]}>
      <p className="help">{TOPICS_TEXT.what[lang]}</p>
      <p className="help">{TOPICS_TEXT.matching[lang]}</p>
      <ul className="faqs">
        {topics.map((topic, i) => {
          const generic = topic.triggers.filter(isGenericTrigger);
          const shades = shadowed(topics, i);
          const oversize = topic.title.length > TOPIC_LIMITS.title || topic.answer.length > TOPIC_LIMITS.answer || topic.triggers.length > TOPIC_LIMITS.triggers || topic.triggers.some((x) => x.length > TOPIC_LIMITS.trigger);
          return (
            <li key={i} className="faq">
              <fieldset>
                <legend>{topic.title || t("topics.untitled", { n: String(i + 1) })}</legend>
                <TextInput label={t("topics.name")} value={topic.title} onChange={(v) => set(i, { title: v })} max={TOPIC_LIMITS.title} help={t("topics.name.help")} />
                <ListEditor
                  label={t("topics.triggers")}
                  items={topic.triggers}
                  onChange={(v) => set(i, { triggers: v })}
                  max={TOPIC_LIMITS.triggers}
                  maxLength={TOPIC_LIMITS.trigger}
                  addLabel={t("topics.addTrigger")}
                  errorPrefix={`knowledge.topics.${i}.triggers`}
                  placeholder={t("topics.trigger.placeholder")}
                />
                <TextInput label={t("topics.answer")} multiline rows={4} value={topic.answer} onChange={(v) => set(i, { answer: v })} max={TOPIC_LIMITS.answer} help={t("topics.answer.help")} />
                <Toggle label={t("topics.on")} checked={topic.enabled !== false} onChange={(v) => set(i, { enabled: v })} />
                {generic.length > 0 && <p className="hint">! {PANEL.warnings.topic_generic[lang]} ({generic.join(", ")})</p>}
                {shades.map((s) => <p className="hint" key={s.trigger}>! {t("topics.shadowed", { trigger: s.trigger, by: s.by })}</p>)}
                {hasPlaceholder(topic.answer) && <p className="hint">! {t("topics.placeholder")}</p>}
                {oversize && <p className="hint">! {t("topics.oversize")}</p>}
                {(!topic.title.trim() || topic.triggers.length === 0 || !topic.answer.trim()) && <p className="hint">{t("topics.incomplete")}</p>}
                <div className="row">
                  <button type="button" className="btn ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t("topics.up", { title: topic.title || String(i + 1) })}>↑</button>
                  <button type="button" className="btn ghost" disabled={i === topics.length - 1} onClick={() => move(i, 1)} aria-label={t("topics.down", { title: topic.title || String(i + 1) })}>↓</button>
                  <button type="button" className="btn ghost" onClick={() => onChange(topics.filter((_, j) => j !== i))}>{t("topics.remove")}</button>
                </div>
              </fieldset>
            </li>
          );
        })}
      </ul>
      <p className="sr-only" role="status" aria-live="polite">{moved}</p>
      <div className="row">
        <button type="button" className="btn secondary" disabled={topics.length >= TOPIC_LIMITS.count} onClick={() => onChange([...topics, { title: "", triggers: [], answer: "", enabled: true }])}>{t("topics.add")}</button>
        <span className="counter">{topics.length}/{TOPIC_LIMITS.count}</span>
      </div>
      <div className="row" role="group" aria-label={t("topics.examples")}>
        <span className="hint">{t("topics.examples")}</span>
        {TOPICS_TEXT.examples.map((e) => (
          <button
            key={e.id}
            type="button"
            className="btn secondary"
            disabled={topics.length >= TOPIC_LIMITS.count}
            onClick={() => onChange([...topics, { title: e.title[lang], triggers: [...e.triggers[lang]], answer: e.answer[lang], enabled: true }])}
          >
            {e.title[lang]}
          </button>
        ))}
      </div>
      {topics.length > 0 && (
        <Field label={t("topics.test")} htmlFor="topic-probe" help={t("topics.test.help")}>
          <input id="topic-probe" type="text" value={probe} onChange={(e) => setProbe(e.target.value)} autoComplete="off" />
          <div role="status" aria-live="polite">
            {probe.trim() !== "" && (hit ? <p><strong>{t("topics.test.hit", { title: hit.title })}</strong> {hit.answer}</p> : <p>{t("topics.test.miss")}</p>)}
          </div>
        </Field>
      )}
    </Section>
  );
}
